import React, { useEffect, useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Card,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { useGetActivityLogQuery } from "../../../Api/attoDeskApi";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import Pagination from "../../../Components/Pagination";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";
import { CiSearch } from "react-icons/ci";
import dayjs from "dayjs";

interface IActivityLog {
  id: string;
  date: string;
  userName: string;
  action: string;
  entityType: string;
  entityId: string;
  description: string;
  logType: string;
}

const entityTypeOptions = [
  { value: "", label: "All Entity Types" },
  { value: "Student", label: "Student" },
  { value: "Staff", label: "Staff" },
  { value: "Class", label: "Class" },
  { value: "Payment", label: "Payment" },
  { value: "Invoice", label: "Invoice" },
  { value: "Attendance", label: "Attendance" },
  { value: "User", label: "User" },
];

const logTypeOptions = [
  { value: "", label: "All Log Types" },
  { value: "CREATE", label: "Create" },
  { value: "UPDATE", label: "Update" },
  { value: "DELETE", label: "Delete" },
  { value: "LOGIN", label: "Login" },
  { value: "LOGOUT", label: "Logout" },
];

const Index = () => {
  const classes = useStyles();
  const { showErrorMessage } = useNotifier();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [logTypeFilter, setLogTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data, isLoading, isError } = useGetActivityLogQuery({
    entityType: entityTypeFilter,
    logType: logTypeFilter,
    dateFrom: dateFrom,
    dateTo: dateTo,
    page: page,
    limit: rowsPerPage,
  });

  const logs: IActivityLog[] = useMemo(() => {
    return Array.isArray(data?.data) ? (data!.data as IActivityLog[]) : [];
  }, [data?.data]);

  const totalCount = useMemo(() => {
    return (data as any)?.totalCount || logs.length;
  }, [(data as any)?.totalCount, logs.length]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
    );

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={2}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Activity Log
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Entity Type"
                value={entityTypeFilter}
                onChange={(e) => {
                  setEntityTypeFilter(e.target.value);
                  setPage(0);
                }}
                className={classes.searchTextField}
              >
                {entityTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Log Type"
                value={logTypeFilter}
                onChange={(e) => {
                  setLogTypeFilter(e.target.value);
                  setPage(0);
                }}
                className={classes.searchTextField}
              >
                {logTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppDatePicker
                fullWidth
                size="small"
                label="Date From"
                value={dateFrom}
                onChange={(v) => {
                  setDateFrom(v);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppDatePicker
                fullWidth
                size="small"
                label="Date To"
                value={dateTo}
                onChange={(v) => {
                  setDateTo(v);
                  setPage(0);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheaderId}>
                  <strong>#</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Date</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>User</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Action</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Entity Type</strong>
                </TableCell>
                <TableCell className={classes.tablheaderId}>
                  <strong>Entity ID</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Description</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isError || logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {isError ? "Error fetching data" : "No activity logs found"}
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((row: IActivityLog, index) => (
                  <TableRow key={row.id} className={classes.tableRowHover}>
                    <TableCell scope="row" className={classes.tablCellId}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell scope="row" className={classes.tablCell}>
                      {row.date ? dayjs(row.date).format("YYYY-MM-DD HH:mm") : ""}
                    </TableCell>
                    <TableCell scope="row" className={classes.tablCell}>
                      {row.userName || ""}
                    </TableCell>
                    <TableCell scope="row" className={classes.tablCell}>
                      {row.action || ""}
                    </TableCell>
                    <TableCell scope="row" className={classes.tablCell}>
                      {row.entityType || ""}
                    </TableCell>
                    <TableCell scope="row" className={classes.tablCellId}>
                      {row.entityId || ""}
                    </TableCell>
                    <TableCell scope="row" className={classes.tablCell}>
                      {row.description || ""}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid
          spacing={5}
          p={"18px"}
          py={4}
          justifyContent={"end"}
          container
          textAlign={"center"}
          alignContent={"center"}
          alignItems={"center"}
        >
          <Grid
            sx={{
              textAlign: "end",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <Pagination
              count={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
            />
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

export default Index;
